// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'
import {
  RefreshCw,
  AlertCircle,
  Play,
  CheckCircle2,
  XCircle,
  Trash2,
  FileText,
  Copy,
  Check,
  AlertTriangle,
  Download,
  Upload,
  Shield,
  Eye,
  EyeOff,
  HelpCircle,
  Lock,
  KeyRound,
  Database,
  HardDrive,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import {
  getRestoreConfig,
  listSnapshots,
  uploadSnapshot,
  runRestore,
  deleteSnapshot,
  getSnapshotLog,
  exportData,
  getDownloadUrl,
  listBaseImages,
  importBaseImage,
} from '../api/data-restore'
import type { BaseImage, RestoreConfig, RestoreSnapshot, ExportOptions } from '../api/types/data-restore'

// Poll interval when a restore is running (ms)
const POLL_INTERVAL = 3000

// Origin-scoped localStorage for encryption keys — attacker needs both S3 zip and browser access
const KEY_PREFIX = 'docreview:encryption-key:'

function saveEncryptionKey(snapshotId: string, key: string): void {
  localStorage.setItem(`${KEY_PREFIX}${snapshotId}`, key)
}

function loadEncryptionKey(snapshotId: string): string | null {
  return localStorage.getItem(`${KEY_PREFIX}${snapshotId}`)
}

function StatusBadge({ status }: { status: RestoreSnapshot['status'] }) {
  switch (status) {
    case 'ready':
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <CheckCircle2 className="h-3 w-3" />
          Ready
        </Badge>
      )
    case 'exporting':
      return (
        <Badge variant="default" className="gap-1 bg-blue-600">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Exporting
        </Badge>
      )
    case 'restoring':
      return (
        <Badge variant="default" className="gap-1 bg-blue-600">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Restoring
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      )
  }
}

function SourceBadge({ source }: { source: RestoreSnapshot['source'] }) {
  const label = source === 'upload' ? 'Upload' : source === 'base_image' ? 'Base Image' : 'Export'
  return (
    <Badge variant="outline" className="text-xs">
      {label}
    </Badge>
  )
}

// Format bytes to MB for display
function formatSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—'
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}

export function DataRestoreManager() {
  const [config, setConfig] = useState<RestoreConfig | null>(null)
  const [snapshots, setSnapshots] = useState<RestoreSnapshot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Upload state
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Export state
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeApp: true,
    includeJobs: false,
    includeCosts: false,
  })
  const [isExporting, setIsExporting] = useState(false)

  // Encryption key — displayed once after export, ephemeral
  const [encryptionKey, setEncryptionKey] = useState<string | null>(null)
  const [isKeyVisible, setIsKeyVisible] = useState(false)
  const [isKeyCopied, setIsKeyCopied] = useState(false)

  // Run confirmation dialog
  const [runDialogOpen, setRunDialogOpen] = useState(false)
  const [snapshotToRun, setSnapshotToRun] = useState<RestoreSnapshot | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [restoreKey, setRestoreKey] = useState('')
  const [isRestoreKeyVisible, setIsRestoreKeyVisible] = useState(false)
  const [shouldSaveKey, setShouldSaveKey] = useState(false)

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [snapshotToDelete, setSnapshotToDelete] = useState<RestoreSnapshot | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Log view dialog
  const [logDialogOpen, setLogDialogOpen] = useState(false)
  const [logContent, setLogContent] = useState<string>('')
  const [logSnapshotId, setLogSnapshotId] = useState<string>('')
  const [isLoadingLog, setIsLoadingLog] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  // Download instructions dialog
  const [downloadInstructionsSnapshot, setDownloadInstructionsSnapshot] = useState<RestoreSnapshot | null>(null)
  const [isCommandCopied, setIsCommandCopied] = useState(false)

  // Security info dialog
  const [securityInfoOpen, setSecurityInfoOpen] = useState(false)

  // Base images state
  const [baseImages, setBaseImages] = useState<BaseImage[]>([])
  const [showBaseImages, setShowBaseImages] = useState(false)
  const [importingKey, setImportingKey] = useState<string | null>(null)

  // Polling state
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Load config and snapshots
  const loadData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true)
      } else {
        setIsLoading(true)
      }
      setError(null)

      const configData = await getRestoreConfig()
      setConfig(configData)

      // Load snapshots if either feature is enabled
      if (configData.restoreEnabled || configData.downloadEnabled) {
        const snaps = await listSnapshots()
        setSnapshots(snaps)
      }

      // Load base images in sandbox
      if (configData.baseImagesAvailable) {
        const images = await listBaseImages()
        setBaseImages(images)
      }
    } catch (err) {
      console.error('[DataRestoreManager] Failed to load:', err)
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [])

  // Check if any job is running (exporting or restoring)
  const hasRunningJob = snapshots.some(s => s.status === 'exporting' || s.status === 'restoring')

  // Start/stop polling based on running state
  useEffect(() => {
    if (hasRunningJob && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => {
        loadData(true)
      }, POLL_INTERVAL)
    } else if (!hasRunningJob && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [hasRunningJob, loadData])

  // Initial load
  useEffect(() => {
    loadData()
  }, [loadData])

  // Handle file upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setUploadProgress(null)
    setError(null)

    try {
      await uploadSnapshot(file, (percent) => {
        setUploadProgress(percent)
      })
      await loadData(true)
    } catch (err) {
      console.error('[DataRestoreManager] Upload failed:', err)
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setIsUploading(false)
      setUploadProgress(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Handle run restore
  const handleRunClick = (snapshot: RestoreSnapshot) => {
    const savedKey = loadEncryptionKey(snapshot.id)
    setSnapshotToRun(snapshot)
    setRestoreKey(savedKey ?? '')
    setShouldSaveKey(!!savedKey)
    setIsRestoreKeyVisible(false)
    setRunDialogOpen(true)
  }

  const handleRunConfirm = async () => {
    if (!snapshotToRun) return
    if (!restoreKey.trim()) {
      setError('Encryption key is required to restore')
      return
    }

    setIsRunning(true)
    try {
      const key = restoreKey.trim()
      if (shouldSaveKey) {
        saveEncryptionKey(snapshotToRun.id, key)
      } else {
        localStorage.removeItem(`${KEY_PREFIX}${snapshotToRun.id}`)
      }
      await runRestore(snapshotToRun.id, key)
      await loadData(true)
      setRunDialogOpen(false)
      setSnapshotToRun(null)
    } catch (err) {
      console.error('[DataRestoreManager] Failed to run restore:', err)
      setError(err instanceof Error ? err.message : 'Failed to run restore')
    } finally {
      setIsRunning(false)
    }
  }

  // Handle delete
  const handleDeleteClick = (snapshot: RestoreSnapshot) => {
    setSnapshotToDelete(snapshot)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!snapshotToDelete) return

    setIsDeleting(true)
    try {
      await deleteSnapshot(snapshotToDelete.id)
      await loadData(true)
      setDeleteDialogOpen(false)
      setSnapshotToDelete(null)
    } catch (err) {
      console.error('[DataRestoreManager] Failed to delete:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle view log
  const handleViewLog = async (snapshot: RestoreSnapshot) => {
    setLogSnapshotId(snapshot.id)
    setLogDialogOpen(true)
    setIsLoadingLog(true)
    setIsCopied(false)
    try {
      const content = await getSnapshotLog(snapshot.id)
      setLogContent(content)
    } catch (err) {
      console.error('[DataRestoreManager] Failed to load log:', err)
      setLogContent('Failed to load log')
    } finally {
      setIsLoadingLog(false)
    }
  }

  // Handle copy to clipboard
  const handleCopyLog = async () => {
    try {
      await navigator.clipboard.writeText(logContent)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('[DataRestoreManager] Failed to copy:', err)
    }
  }

  // Handle export
  const handleExport = async () => {
    if (!exportOptions.includeApp && !exportOptions.includeJobs && !exportOptions.includeCosts) {
      setError('Select at least one data source to export')
      return
    }

    setIsExporting(true)
    setError(null)
    setEncryptionKey(null)

    try {
      const response = await exportData(exportOptions)
      setEncryptionKey(response.encryptionKey)
      setIsKeyVisible(false)
      setIsKeyCopied(false)
      await loadData(true)
    } catch (err) {
      console.error('[DataRestoreManager] Export failed:', err)
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setIsExporting(false)
    }
  }

  // Handle download — fetch presigned URL with auth, then open it
  const handleDownload = async (snapshot: RestoreSnapshot) => {
    try {
      const url = await getDownloadUrl(snapshot.id)
      window.open(url, '_blank')
      setDownloadInstructionsSnapshot(snapshot)
      setIsCommandCopied(false)
    } catch (err) {
      console.error('[DataRestoreManager] Download failed:', err)
      setError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  // Handle base image import
  const handleImportBaseImage = async (key: string) => {
    setImportingKey(key)
    setError(null)
    try {
      await importBaseImage(key)
      await loadData(true)
    } catch (err) {
      console.error('[DataRestoreManager] Import failed:', err)
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setImportingKey(null)
    }
  }

  // Format date for display
  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Never'
    return new Date(isoString).toLocaleString()
  }

  // If neither feature is enabled, show disabled state
  if (!isLoading && config && !config.restoreEnabled && !config.downloadEnabled) {
    return (
      <div className="text-slate-900">
        <div className="space-y-5">
          <div>
            <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Data Restore</h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              Export and restore environment data
            </p>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Disabled in {config.environment}</AlertTitle>
            <AlertDescription>
              Data restore/export is not enabled in this environment.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="text-slate-900">
      <div className="space-y-5">
        {/* Header Section */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Data Restore</h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              Snapshot this environment&apos;s data as a downloadable export, or restore a
              snapshot into it — used for backups and copying environments.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSecurityInfoOpen(true)}
              aria-label="Security information"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Warning Banner - only show if restore is enabled */}
        {config?.restoreEnabled && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Destructive Operation</AlertTitle>
            <AlertDescription>
              Restoring a snapshot will <strong>DELETE ALL EXISTING DATA</strong> in DynamoDB
              and S3, then import the snapshot data. This cannot be undone.
            </AlertDescription>
          </Alert>
        )}

        {/* Export and Upload Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Export Section - only show if download is enabled */}
          {config?.downloadEnabled && (
            <Card className="rounded-xl border-[#e7ecf2] shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg">Export Current Data</CardTitle>
                <CardDescription>
                  Create snapshot from this environment&apos;s data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">DynamoDB Tables:</Label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-app"
                        checked={exportOptions.includeApp}
                        onCheckedChange={(checked) =>
                          setExportOptions((prev) => ({ ...prev, includeApp: !!checked }))
                        }
                      />
                      <Label htmlFor="include-app" className="text-sm cursor-pointer">
                        App
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-jobs"
                        checked={exportOptions.includeJobs}
                        onCheckedChange={(checked) =>
                          setExportOptions((prev) => ({ ...prev, includeJobs: !!checked }))
                        }
                      />
                      <Label htmlFor="include-jobs" className="text-sm cursor-pointer">
                        Jobs
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-costs"
                        checked={exportOptions.includeCosts}
                        onCheckedChange={(checked) =>
                          setExportOptions((prev) => ({ ...prev, includeCosts: !!checked }))
                        }
                      />
                      <Label htmlFor="include-costs" className="text-sm cursor-pointer">
                        Costs
                      </Label>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleExport}
                  disabled={isExporting || hasRunningJob}
                  className="w-full"
                >
                  {isExporting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Export
                    </>
                  )}
                </Button>

                {/* Encryption key — inline after export action */}
                {encryptionKey && (
                  <div className="space-y-2 rounded-md border bg-muted/50 p-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Shield className="h-4 w-4" />
                      AES-256 Encrypted
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded-md border bg-background px-3 py-2 font-mono text-sm text-foreground tracking-wider">
                        {isKeyVisible ? encryptionKey : '••••••••••••••••••••••••••••••'}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setIsKeyVisible(!isKeyVisible)}
                        aria-label={isKeyVisible ? 'Hide key' : 'Show key'}
                      >
                        {isKeyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={async () => {
                          await navigator.clipboard.writeText(encryptionKey)
                          setIsKeyCopied(true)
                          setTimeout(() => setIsKeyCopied(false), 2000)
                        }}
                        aria-label="Copy encryption key"
                      >
                        {isKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-destructive">
                      Copy this key now — it cannot be recovered after leaving this page.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Upload Section - only show if restore is enabled */}
          {config?.restoreEnabled && (
            <Card className="rounded-xl border-[#e7ecf2] shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg">Upload Snapshot</CardTitle>
                <CardDescription>
                  Upload a zip file containing exported data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="max-w-md text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 disabled:opacity-50"
                  />
                  {isUploading && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      {uploadProgress !== null
                        ? `Uploading... ${uploadProgress}%`
                        : 'Preparing upload...'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Base Images — sandbox only */}
        {config?.baseImagesAvailable && !isLoading && baseImages.length > 0 && (
          <div>
            <button
              onClick={() => setShowBaseImages(!showBaseImages)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
            >
              {showBaseImages ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Database className="h-4 w-4" />
              Shared Base Images ({baseImages.length})
            </button>

            {showBaseImages && (
              <div className="grid gap-3">
                {baseImages.map(image => (
                  <Card key={image.key} className="rounded-xl border-[#e7ecf2] shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
                    <CardContent className="flex items-center justify-between py-3 px-4">
                      <div className="space-y-1">
                        <p className="text-sm font-mono font-medium">{image.key}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatSize(image.sizeBytes)} · {new Date(image.lastModified).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImportBaseImage(image.key)}
                        disabled={importingKey !== null}
                      >
                        {importingKey === image.key ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Import
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading snapshots...</span>
          </div>
        )}

        {/* Snapshots List */}
        {!isLoading && snapshots.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No snapshots yet. {config?.downloadEnabled ? 'Export data or upload' : 'Upload'} a zip file to get started.
          </div>
        )}

        {!isLoading && snapshots.length > 0 && (
          <div className="grid gap-4">
            {snapshots.map(snapshot => (
              <Card key={snapshot.id} className="rounded-xl border-[#e7ecf2] shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg">{snapshot.name}</CardTitle>
                      <StatusBadge status={snapshot.status} />
                      <SourceBadge source={snapshot.source} />
                      {snapshot.source === 'export' && (
                        <Badge variant="outline" className="gap-1 text-xs">
                          <Shield className="h-3 w-3" />
                          AES-256
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {snapshot.logKey && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewLog(snapshot)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View Log
                        </Button>
                      )}
                      {config?.downloadEnabled && snapshot.status === 'ready' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(snapshot)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(snapshot)}
                        disabled={snapshot.status === 'restoring'}
                        aria-label="Delete snapshot"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {config?.restoreEnabled && (
                        <Button
                          size="sm"
                          onClick={() => handleRunClick(snapshot)}
                          disabled={snapshot.status !== 'ready' || hasRunningJob}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Restore
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardDescription className="font-mono text-xs">
                    {snapshot.id}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Created: </span>
                      <span>{formatDate(snapshot.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Size: </span>
                      <span>{formatSize(snapshot.sizeBytes)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {snapshot.appItems > 0 && (
                      <Badge variant="outline" className="text-xs">
                        App: {snapshot.appItems}
                      </Badge>
                    )}
                    {snapshot.jobsItems > 0 && (
                      <Badge variant="outline" className="text-xs">
                        Jobs: {snapshot.jobsItems}
                      </Badge>
                    )}
                    {snapshot.costsItems > 0 && (
                      <Badge variant="outline" className="text-xs">
                        Costs: {snapshot.costsItems}
                      </Badge>
                    )}
                  </div>

                  {snapshot.error && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="font-mono text-xs">
                        {snapshot.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Run Confirmation Dialog */}
        <AlertDialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">
                Restore Data
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>
                    Restore from{' '}
                    <span className="font-semibold font-mono">{snapshotToRun?.id}</span>?
                  </p>
                  <p className="font-semibold text-destructive">
                    This will DELETE ALL EXISTING DATA in DynamoDB and S3, then import
                    the snapshot data. This action cannot be undone.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="restore-key" className="text-sm font-medium text-foreground">
                      Encryption Key
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="restore-key"
                        type={isRestoreKeyVisible ? 'text' : 'password'}
                        value={restoreKey}
                        onChange={(e) => setRestoreKey(e.target.value)}
                        placeholder="Paste the encryption key"
                        className="flex-1 font-mono"
                        autoComplete="off"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        onClick={() => setIsRestoreKeyVisible(!isRestoreKeyVisible)}
                        aria-label={isRestoreKeyVisible ? 'Hide key' : 'Show key'}
                      >
                        {isRestoreKeyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="save-key"
                        checked={shouldSaveKey}
                        onCheckedChange={(checked) => setShouldSaveKey(!!checked)}
                        disabled={!restoreKey.trim()}
                      />
                      <Label htmlFor="save-key" className="text-xs text-muted-foreground cursor-pointer">
                        Remember key in this browser
                      </Label>
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isRunning}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRunConfirm}
                disabled={isRunning || !restoreKey.trim()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isRunning ? 'Starting...' : 'Yes, Restore Data'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Snapshot</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{' '}
                <span className="font-semibold font-mono">{snapshotToDelete?.id}</span>?
                This will remove the uploaded snapshot files from S3.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Log View Dialog */}
        <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-mono text-sm pr-16">
                {logSnapshotId}
              </DialogTitle>
            </DialogHeader>
            <div className="flex justify-end -mt-2 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLog}
                disabled={isLoadingLog}
                className="gap-2"
              >
                {isCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="flex-1 overflow-auto">
              {isLoadingLog ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading log...</span>
                </div>
              ) : (
                <pre className="bg-muted p-4 rounded text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                  {logContent}
                </pre>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Download Instructions Dialog */}
        <Dialog open={!!downloadInstructionsSnapshot} onOpenChange={() => setDownloadInstructionsSnapshot(null)}>
          <DialogContent className="max-w-lg" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Upload to Base Images
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Upload the downloaded snapshot to the shared base images bucket, then delete
                the local file.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">AWS CLI Command</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={async () => {
                      const cmd = `aws s3 cp ~/Downloads/${downloadInstructionsSnapshot?.id}.zip s3://docreview-base-images/${downloadInstructionsSnapshot?.id}.zip`
                      await navigator.clipboard.writeText(cmd)
                      setIsCommandCopied(true)
                      setTimeout(() => setIsCommandCopied(false), 2000)
                    }}
                  >
                    {isCommandCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {isCommandCopied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`aws s3 cp ~/Downloads/${downloadInstructionsSnapshot?.id}.zip \\
  s3://docreview-base-images/${downloadInstructionsSnapshot?.id}.zip`}
                </pre>
              </div>
              <div className="rounded-md border bg-destructive/10 p-3">
                <p className="text-xs text-destructive font-medium">
                  Delete the downloaded file from your local disk after uploading to S3.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Security Info Dialog */}
        <Dialog open={securityInfoOpen} onOpenChange={setSecurityInfoOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Export &amp; Restore Security
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 text-sm">
              {/* Overview */}
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold">
                  <Lock className="h-4 w-4" />
                  Overview
                </h3>
                <p className="text-muted-foreground">
                  Exported snapshots are encrypted with AES-256, the same standard used by
                  governments and financial institutions. Even if an attacker obtains the zip
                  file from S3, they cannot read the contents without the encryption key.
                </p>
              </section>

              {/* How Export Works */}
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 font-semibold">
                  <Upload className="h-4 w-4" />
                  How Export Works
                </h3>
                <ol className="space-y-2 text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">1</span>
                    <span>You click <strong className="text-foreground">Export</strong> and select which DynamoDB tables and S3 files to include.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">2</span>
                    <span>The server generates a <strong className="text-foreground">30-character random key</strong> using Python&apos;s cryptographic <code className="text-xs bg-muted px-1 rounded">secrets.token_urlsafe</code> module.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">3</span>
                    <span>The jobs lambda creates the zip using <strong className="text-foreground">AES-256 encryption</strong> (WinZip AES standard via pyzipper).</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">4</span>
                    <span>The key is returned <strong className="text-foreground">once</strong> in the API response and displayed in the UI. It is never stored on the server.</span>
                  </li>
                </ol>
              </section>

              {/* How Restore Works */}
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 font-semibold">
                  <Download className="h-4 w-4" />
                  How Restore Works
                </h3>
                <ol className="space-y-2 text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">1</span>
                    <span>You click <strong className="text-foreground">Restore</strong> on a snapshot and enter the encryption key.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">2</span>
                    <span>The key is sent to the jobs lambda, which decrypts the zip and imports the data into DynamoDB and S3.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">3</span>
                    <span>Optionally, you can check <strong className="text-foreground">Remember key in this browser</strong> to save it in localStorage for convenience.</span>
                  </li>
                </ol>
              </section>

              {/* Key Storage */}
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold">
                  <KeyRound className="h-4 w-4" />
                  Key Storage
                </h3>
                <p className="text-muted-foreground">
                  When you opt in, the key is saved in your browser&apos;s <strong className="text-foreground">localStorage</strong>,
                  which is scoped to this origin. No other website can access it. The key persists
                  across browser restarts and is only accessible from this application.
                </p>
              </section>

              {/* Defense in Depth */}
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold">
                  <Shield className="h-4 w-4" />
                  Defense in Depth
                </h3>
                <p className="text-muted-foreground">
                  An attacker would need <strong className="text-foreground">both</strong> the encrypted zip from S3{' '}
                  <strong className="text-foreground">and</strong> the key from the user&apos;s browser. Compromising
                  one without the other is insufficient.
                </p>
                <div className="flex gap-4 mt-2">
                  <div className="flex-1 rounded-md border bg-muted/50 p-3 text-center">
                    <Database className="h-5 w-5 mx-auto mb-1" />
                    <p className="text-xs font-medium text-foreground">Data at rest</p>
                    <p className="text-xs text-muted-foreground">Encrypted zip in S3</p>
                  </div>
                  <div className="flex items-center text-muted-foreground text-lg font-bold">+</div>
                  <div className="flex-1 rounded-md border bg-muted/50 p-3 text-center">
                    <HardDrive className="h-5 w-5 mx-auto mb-1" />
                    <p className="text-xs font-medium text-foreground">Key at rest</p>
                    <p className="text-xs text-muted-foreground">User&apos;s browser only</p>
                  </div>
                </div>
              </section>

              {/* AES-256 Strength */}
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold">
                  <Lock className="h-4 w-4" />
                  AES-256 Strength
                </h3>
                <p className="text-muted-foreground">
                  AES-256 uses a 256-bit key space (2<sup>256</sup> possible keys). The 30-character
                  key generated by <code className="text-xs bg-muted px-1 rounded">secrets.token_urlsafe</code> provides
                  approximately 178 bits of entropy. Even at one trillion guesses per second,
                  brute-forcing 178 bits of entropy would take longer than the age of the universe.
                </p>
              </section>

              {/* Data Sensitivity Note */}
              <section className="rounded-md border bg-muted/50 p-4 space-y-1">
                <p className="text-xs font-medium text-foreground">
                  Note on data sensitivity
                </p>
                <p className="text-xs text-muted-foreground">
                  DocReview does not store sensitive personally identifiable information (SSN,
                  financial data, etc.) in DynamoDB or S3. The exported data consists of workspace
                  configurations, document metadata, analysis results, and pipeline definitions.
                  Encryption provides an additional layer of protection for organizational data.
                </p>
              </section>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
